import { useState } from 'react';
import { BookOpen, MessageSquare, Calendar, ChevronDown, Ship, Anchor, Star } from 'lucide-react';
import { normalizeNote, moyenneNormalisee } from '../utils/normalizeNote';

const SUPPORT_ICONS = { 'VNM': Ship, 'ENGINS TRACTÉS': Anchor, 'ENGINS TRACTES': Anchor };

const CarnetNotesFormateur = ({ notes = [], title = "Carnet de Notes - Formateur", hidePrivateNotes = false }) => {
  const [expandedSupport, setExpandedSupport] = useState(null);

  // Group notes by SUPPORT then by categorie
  const supportsMap = {};
  notes.forEach(n => {
    const cat = n.categorie || 'Autre';
    const item = n.item || '';
    const support = item.toUpperCase().replace('BRIEFING ', '').replace('AISANCE ', '').replace('ENCADREMENT ', '').trim() || item || 'Autre';
    if (!supportsMap[support]) supportsMap[support] = {};
    if (!supportsMap[support][cat]) supportsMap[support][cat] = [];
    supportsMap[support][cat].push(n);
  });

  const supports = Object.keys(supportsMap).sort();
  const moyenneGenerale = notes.length > 0 ? moyenneNormalisee(notes).toFixed(1) : 0;

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }) : '';
  
  const badgeStyle = (v) => {
    if (v >= 8) return 'bg-green-100 text-green-700 border-green-300';
    if (v >= 5) return 'bg-orange-100 text-orange-700 border-orange-300';
    return 'bg-red-100 text-red-700 border-red-300';
  };

  const catBadge = (cat) => {
    if (cat === 'Aisance') return 'text-sky-700 bg-sky-50 border-sky-200';
    if (cat === 'Briefing') return 'text-amber-700 bg-amber-50 border-amber-200';
    if (cat === 'Encadrement') return 'text-emerald-700 bg-emerald-50 border-emerald-200';
    return 'text-slate-600 bg-slate-50 border-slate-200';
  };

  const stats = {
    total: notes.length,
    excellent: notes.filter(n => normalizeNote(n) >= 8).length,
    moyen: notes.filter(n => normalizeNote(n) >= 5 && normalizeNote(n) < 8).length,
    insuffisant: notes.filter(n => normalizeNote(n) < 5).length
  };

  return (
    <div className="space-y-4" data-testid="carnet-notes-formateur">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-display font-semibold text-[#0F172A] flex items-center gap-2">
          <BookOpen className="h-5 w-5 text-[#8DC63F]" />
          {title}
        </h2>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-5 gap-3">
        <div className="text-center p-3 bg-[#8DC63F]/10 rounded-lg border border-[#8DC63F]/30">
          <p className="text-xl font-bold text-[#8DC63F]">{moyenneGenerale}/10</p>
          <p className="text-xs text-slate-600">Moyenne</p>
        </div>
        <div className="text-center p-3 bg-slate-50 rounded-lg border border-slate-200">
          <p className="text-xl font-bold text-[#0F172A]">{stats.total}</p>
          <p className="text-xs text-slate-600">Total</p>
        </div>
        <div className="text-center p-3 bg-green-50 rounded-lg border border-green-200">
          <p className="text-xl font-bold text-green-600">{stats.excellent}</p>
          <p className="text-xs text-green-700">&ge; 8/10</p>
        </div>
        <div className="text-center p-3 bg-orange-50 rounded-lg border border-orange-200">
          <p className="text-xl font-bold text-orange-600">{stats.moyen}</p>
          <p className="text-xs text-orange-700">5-7/10</p>
        </div>
        <div className="text-center p-3 bg-red-50 rounded-lg border border-red-200">
          <p className="text-xl font-bold text-red-600">{stats.insuffisant}</p>
          <p className="text-xs text-red-700">&lt; 5/10</p>
        </div>
      </div>

      {/* Supports */}
      {supports.length === 0 ? (
        <div className="text-center py-8">
          <BookOpen className="h-12 w-12 text-slate-300 mx-auto mb-2" />
          <p className="text-slate-500">Aucune note du formateur</p>
        </div>
      ) : (
        <div className="space-y-3">
          {supports.map(support => {
            const catsMap = supportsMap[support];
            const allNotes = Object.values(catsMap).flat();
            const supMoy = moyenneNormalisee(allNotes).toFixed(1);
            const isExpanded = expandedSupport === support;
            const IconComp = SUPPORT_ICONS[support] || Star;

            return (
              <div key={support} className="border border-slate-200 rounded-lg overflow-hidden">
                {/* Support header */}
                <div
                  className="flex items-center justify-between p-4 bg-gradient-to-r from-[#1e3a5f]/10 to-[#2d5a88]/5 cursor-pointer hover:from-[#1e3a5f]/15 transition-colors"
                  onClick={() => setExpandedSupport(isExpanded ? null : support)}
                  data-testid={`support-toggle-${support}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="p-1.5 bg-[#1e3a5f] rounded-lg">
                      <IconComp className="h-4 w-4 text-[#8DC63F]" />
                    </div>
                    <div>
                      <p className="font-bold text-[#0F172A]">{support}</p>
                      <p className="text-xs text-slate-500">
                        {allNotes.length} note{allNotes.length > 1 ? 's' : ''} | {Object.keys(catsMap).length} axe{Object.keys(catsMap).length > 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-3 py-1 text-sm font-bold rounded-full border ${badgeStyle(parseFloat(supMoy))}`}>
                      {supMoy}/10
                    </span>
                    <ChevronDown className={`h-5 w-5 text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                  </div>
                </div>

                {/* Categories inside support */}
                {isExpanded && (
                  <div className="p-4 bg-white space-y-4">
                    {['Aisance', 'Briefing', 'Encadrement'].filter(c => catsMap[c]).concat(
                      Object.keys(catsMap).filter(c => !['Aisance', 'Briefing', 'Encadrement'].includes(c))
                    ).map(cat => {
                      const catNotes = catsMap[cat];
                      if (!catNotes) return null;
                      const catMoy = moyenneNormalisee(catNotes).toFixed(1);

                      return (
                        <div key={cat} className="border border-slate-100 rounded-lg overflow-hidden">
                          {/* Category title */}
                          <div className="flex items-center justify-between px-4 py-2 bg-slate-50">
                            <span className={`px-2.5 py-1 rounded-md text-xs font-semibold border ${catBadge(cat)}`}>{cat}</span>
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-slate-500">{catNotes.length} note{catNotes.length > 1 ? 's' : ''}</span>
                              <span className={`px-2 py-0.5 text-xs font-bold rounded border ${badgeStyle(parseFloat(catMoy))}`}>{catMoy}/10</span>
                            </div>
                          </div>

                          {/* Notes list */}
                          <div className="divide-y divide-slate-50">
                            {catNotes.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0)).map((note, idx) => (
                              <div key={idx} className="flex items-start gap-3 px-4 py-2.5">
                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-bold border flex-shrink-0 ${badgeStyle(normalizeNote(note))}`}>
                                  {normalizeNote(note).toFixed(0)}/10
                                </span>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 text-xs text-slate-400 mb-0.5">
                                    <Calendar className="h-3 w-3" />
                                    <span>{formatDate(note.date)}</span>
                                    {note.formateur_nom && <span>- {note.formateur_nom}</span>}
                                  </div>
                                  {note.commentaire && (
                                    <div className="mt-1 p-2 bg-slate-50 rounded border-l-2 border-[#8DC63F]">
                                      <p className="text-xs text-slate-700 whitespace-pre-line leading-relaxed">{note.commentaire}</p>
                                    </div>
                                  )}
                                  {!hidePrivateNotes && note.commentaire_prive && (
                                    <div className="mt-1 p-2 bg-blue-50/50 rounded border-l-2 border-blue-400">
                                      <p className="text-xs text-blue-700 flex items-center gap-1 mb-0.5 font-medium"><MessageSquare className="h-3 w-3" />Note interne</p>
                                      <p className="text-xs text-slate-700">{note.commentaire_prive}</p>
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CarnetNotesFormateur;
